import React, { useEffect, useMemo, useState } from 'react';
import {
  ResponsiveContainer,
  ScatterChart, Scatter, XAxis, YAxis, ZAxis, Tooltip,
  BarChart, Bar, CartesianGrid, Legend
} from 'recharts';
import { FaSyncAlt } from 'react-icons/fa';
import {
  getExpansionAndroid,
  getExpansionIOS,
} from '../services/api';
import BubblePack from '../components/BubblePack';
import { useMeasure } from '../hooks/useMeasure';
import * as d3 from 'd3';

const PIN_RE = /\b\d{6}\b/; // Indian pincode
const TIMES = [
  '06:00 AM','06:30 AM','07:00 AM','07:30 AM','08:00 AM','08:30 AM',
  '09:00 AM','09:30 AM','10:00 AM','10:30 AM','11:00 AM','11:30 AM',
  '12:00 PM','12:30 PM','01:00 PM','01:30 PM','02:00 PM','02:30 PM',
  '03:00 PM','03:30 PM','04:00 PM','04:30 PM','05:00 PM','05:30 PM',
  '06:00 PM','06:30 PM','07:00 PM','07:30 PM','08:00 PM'
];

const card = "bg-white dark:bg-neutral-900 border border-gray-200 dark:border-neutral-800 rounded-2xl shadow";
const labelMuted = "text-sm text-gray-600 dark:text-gray-300";
const textMuted = "text-xs text-gray-500 dark:text-gray-400";

const ReviewAnalytics = () => {
  const [androidRaw, setAndroidRaw] = useState([]);
  const [iosRaw, setIosRaw] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [timeSlots, setTimeSlots] = useState([]); // multi-select
  const [platform, setPlatform] = useState('ALL'); // ALL | ANDROID | IOS

  useEffect(() => {
    const fetchAll = async () => {
      try {
        setLoading(true);
        const [and, ios] = await Promise.all([
          getExpansionAndroid(),
          getExpansionIOS(),
        ]);
        setAndroidRaw(Array.isArray(and) ? and : []);
        setIosRaw(Array.isArray(ios) ? ios : []);
      } catch (e) {
        console.error('ReviewAnalytics: fetch error', e);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  // Helpers
  const normalize = (row) => {
    // expected fields: address, createdAt (timestamp or iso), selectedDate (timestamp), selectedTimeSlot, studentId,...
    const address = row?.address || '';
    const pincode = (address.match(PIN_RE) || [])[0] || 'UNKNOWN';
    // createdAt as JS Date
    let createdAt = null;
    if (row?.createdAt?.seconds) createdAt = new Date(row.createdAt.seconds * 1000);
    else if (row?.createdAt) createdAt = new Date(row.createdAt);
    // selectedDate as JS Date
    let selectedDate = null;
    if (row?.selectedDate?.seconds) selectedDate = new Date(row.selectedDate.seconds * 1000);
    else if (row?.selectedDate) selectedDate = new Date(row.selectedDate);

    return {
      ...row,
      pincode,
      createdAt,
      selectedDate,
      selectedTimeSlot: row?.selectedTimeSlot || '',
    };
  };

  const filtered = useMemo(() => {
    const andN = androidRaw.map(normalize).map(r => ({...r, __platform: 'ANDROID'}));
    const iosN = iosRaw.map(normalize).map(r => ({...r, __platform: 'IOS'}));

    let rows = [...andN, ...iosN];

    if (platform !== 'ALL') {
      rows = rows.filter(r => r.__platform === platform);
    }

    if (fromDate) {
      const d = new Date(fromDate);
      rows = rows.filter(r => (r.selectedDate || r.createdAt) && new Date(r.selectedDate || r.createdAt) >= d);
    }
    if (toDate) {
      const d = new Date(toDate);
      // include end day
      d.setHours(23,59,59,999);
      rows = rows.filter(r => (r.selectedDate || r.createdAt) && new Date(r.selectedDate || r.createdAt) <= d);
    }

    if (timeSlots.length > 0) {
      rows = rows.filter(r => r.selectedTimeSlot && timeSlots.includes(r.selectedTimeSlot));
    }

    return rows;
  }, [androidRaw, iosRaw, fromDate, toDate, timeSlots, platform]);

  // Aggregate per pincode
  const pincodeAgg = useMemo(() => {
    const acc = new Map();
    for (const r of filtered) {
      const key = r.pincode;
      if (!acc.has(key)) acc.set(key, { pincode: key, total: 0, ANDROID: 0, IOS: 0 });
      const obj = acc.get(key);
      obj.total += 1;
      obj[r.__platform] += 1;
      acc.set(key, obj);
    }
    // sort by total desc
    return Array.from(acc.values()).sort((a,b) => b.total - a.total);
  }, [filtered]);

  // Bubble data for Recharts ScatterChart:
  // Use categorical X for pincode and a constant Y, size = total.
  const bubbleDataAll = useMemo(() => pincodeAgg.map((d) => ({
    x: d.pincode,
    y: 1,
    z: d.total,
    total: d.total,
    ANDROID: d.ANDROID,
    IOS: d.IOS,
  })), [pincodeAgg]);

  // Top 10 for bar chart
  const top10 = useMemo(() => pincodeAgg.slice(0, 10).map(d => ({
    pincode: d.pincode,
    total: d.total,
    ANDROID: d.ANDROID,
    IOS: d.IOS,
  })), [pincodeAgg]);

  const resetFilters = () => {
    setFromDate('');
    setToDate('');
    setTimeSlots([]);
    setPlatform('ALL');
  };

  const toggleTime = (t) => {
    setTimeSlots(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);
  };

  const bubblePackData = useMemo(() =>
    pincodeAgg.map(d => ({
      name: d.pincode,     // label
      value: d.total,      // size
      ANDROID: d.ANDROID,
      IOS: d.IOS,
    }))
  , [pincodeAgg]);

  const [packRef, packSize] = useMeasure();
  // pick a height; you can make this dynamic if you like
  const packHeight = 420;

  // optional color function: 0 = all Android (blue), 1 = all iOS (yellow)
  const colorFn = (d) => {
    const total = d.value || 1;
    const iosShare = (d.IOS || 0) / total;
    return d3.interpolateRgb("#3b82f6", "#f59e0b")(iosShare);
  };

  // optional: clicking a bubble sets a pincode filter or navigate somewhere
  const onBubbleClick = (d) => {
    // Example: filter table to that pincode
    // You could also set state like setSelectedPincode(d.name)
    // or navigate(`/bookings?pincode=${d.name}`)
    alert(`Pincode ${d.name} · total ${d.value}`);
  };

  return (
    <div className="p-6 space-y-6 text-gray-800 dark:text-gray-100">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-poppins text-driveeasy-blue dark:text-driveeasy-yellow">
          Expansion Traffic Analytics
        </h2>
        <button onClick={resetFilters}
          className="inline-flex items-center gap-2 px-3 py-2 rounded border border-gray-200 dark:border-neutral-700 hover:bg-gray-50 dark:hover:bg-neutral-800">
          <FaSyncAlt /> Reset
        </button>
      </div>

      {/* Filters */}
      <div className={`${card} p-4`}>
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          <div>
            <div className={labelMuted}>From date</div>
            <input type="date" value={fromDate} onChange={e=>setFromDate(e.target.value)}
              className="w-full mt-1 p-2 rounded border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-800"/>
          </div>
          <div>
            <div className={labelMuted}>To date</div>
            <input type="date" value={toDate} onChange={e=>setToDate(e.target.value)}
              className="w-full mt-1 p-2 rounded border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-800"/>
          </div>
          <div>
            <div className={labelMuted}>Platform</div>
            <select value={platform} onChange={e=>setPlatform(e.target.value)}
              className="w-full mt-1 p-2 rounded border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-800">
              <option value="ALL">All</option>
              <option value="ANDROID">Android</option>
              <option value="IOS">iOS</option>
            </select>
          </div>
          <div>
            <div className={labelMuted}>Time slots (multi)</div>
            <div className="flex flex-wrap gap-2 mt-2 max-h-24 overflow-auto pr-1">
              {TIMES.map(t => (
                <button key={t}
                  onClick={() => toggleTime(t)}
                  className={`px-2 py-1 text-xs rounded border ${
                    timeSlots.includes(t)
                      ? 'bg-driveeasy-blue text-white border-driveeasy-blue'
                      : 'border-gray-300 dark:border-neutral-700'
                  }`}>
                  {t}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className={`${textMuted} mt-2`}>
          Showing {filtered.length} requests · {pincodeAgg.length} pincodes
        </div>
      </div>

      {/* Bubble chart */}
      <div className={`${card} p-4`}>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold">Pincode Bubble Pack (by request volume)</h3>
          <span className={textMuted}>Bigger bubble = more requests (color: iOS share)</span>
        </div>

        <div ref={packRef} className="w-full" style={{ height: packHeight }}>
          <BubblePack
            data={bubblePackData}
            width={Math.max(260, packSize.width)} // min width fallback
            height={packHeight}
            padding={4}
            colorFn={colorFn}           // or omit to use solid blue
            onBubbleClick={onBubbleClick}
          />
        </div>

        <div className="mt-3 text-xs text-gray-600 dark:text-gray-400">
          Color scale: blue = Android-heavy, yellow = iOS-heavy.
        </div>
      </div>

      {/* Top Pincodes bar */}
      <div className={`${card} p-4`}>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold">Top Pincodes</h3>
          <span className={textMuted}>Top 10 by requests</span>
        </div>
        <div className="h-[320px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={top10}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="pincode" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Bar dataKey="total" name="Total" fill="#336fcc" />
              <Bar dataKey="ANDROID" name="Android" fill="#22c55e" />
              <Bar dataKey="IOS" name="iOS" fill="#f59e0b" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Table */}
      <div className={`${card} p-4`}>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold">Pincode Breakdown</h3>
          <span className={textMuted}>Download CSV coming soon</span>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100 dark:bg-neutral-800">
              <tr>
                <th className="text-left p-2">Pincode</th>
                <th className="text-left p-2">Total</th>
                <th className="text-left p-2">Android</th>
                <th className="text-left p-2">iOS</th>
              </tr>
            </thead>
            <tbody>
              {pincodeAgg.map(row => (
                <tr key={row.pincode} className="border-b border-gray-100 dark:border-neutral-800">
                  <td className="p-2">{row.pincode}</td>
                  <td className="p-2 font-semibold">{row.total}</td>
                  <td className="p-2">{row.ANDROID}</td>
                  <td className="p-2">{row.IOS}</td>
                </tr>
              ))}
              {pincodeAgg.length === 0 && !loading && (
                <tr><td colSpan={4} className="p-4 text-center text-gray-500 dark:text-gray-400">No data</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ReviewAnalytics;
